import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Typography, Spin, Alert } from 'antd';
import {
  UserOutlined,
  AppstoreOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { dashboardService, DashboardStats } from '../services/dashboardService';
import { analyticsService } from '../services/analyticsService';
import { getImageUrl } from '../config/api';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  if (!stats) return null;

  const recentPetsColumns = [
    {
      title: 'Pet',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src={getImageUrl(record.image)}
            alt={text}
            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40';
            }}
          />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `₹${price?.toLocaleString() || 0}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
    },
  ];

  return (
    <div>
      <Title level={2}>Dashboard</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: 8 }}>
              <span style={{ color: stats.growthStats.users.trend === 'up' ? '#52c41a' : '#ff4d4f' }}>
                {stats.growthStats.users.trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                {stats.growthStats.users.value}%
              </span>
              <span style={{ marginLeft: 8, color: '#8c8c8c' }}>vs last month</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Pets"
              value={stats.totalPets}
              prefix={<AppstoreOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div style={{ marginTop: 8 }}>
              <span style={{ color: stats.growthStats.pets.trend === 'up' ? '#52c41a' : '#ff4d4f' }}>
                {stats.growthStats.pets.trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                {stats.growthStats.pets.value}%
              </span>
              <span style={{ marginLeft: 8, color: '#8c8c8c' }}>vs last month</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Posts"
              value={stats.activePosts}
              prefix={<MessageOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: 8 }}>
              <span style={{ color: stats.growthStats.posts.trend === 'up' ? '#52c41a' : '#ff4d4f' }}>
                {stats.growthStats.posts.trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                {Math.abs(stats.growthStats.posts.value)}%
              </span>
              <span style={{ marginLeft: 8, color: '#8c8c8c' }}>vs last month</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Listing Views"
              value={stats.dailyViews}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
            <div style={{ marginTop: 8 }}>
              <span style={{ color: '#8c8c8c' }}>across all pet listings</span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Pet marketplace breakdown (admin) */}
      <Title level={4} style={{ marginBottom: 12 }}>Pet Marketplace</Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Total Listings" value={stats.petStats.total} valueStyle={{ color: '#1677ff' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Available" value={stats.petStats.available} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Sold / Hidden" value={stats.petStats.sold} valueStyle={{ color: '#ff4d4f' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Featured" value={stats.petStats.featured} valueStyle={{ color: '#faad14' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Premium" value={stats.petStats.premium} valueStyle={{ color: '#722ed1' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Pending Approval" value={stats.petStats.pendingApproval} valueStyle={{ color: '#fa8c16' }} /></Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Recent Pets" extra={<EyeOutlined />}>
            <Table
              dataSource={stats.recentPets}
              columns={recentPetsColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Top Categories">
            {stats.topCategories.map((category, index) => (
              <div key={index} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span>{category.name}</span>
                  <span>{category.count}</span>
                </div>
                <div style={{ background: '#f0f0f0', borderRadius: 4, height: 8 }}>
                  <div
                    style={{
                      background: '#1890ff',
                      height: '100%',
                      width: `${category.percentage}%`,
                      borderRadius: 4,
                    }}
                  />
                </div>
              </div>
            ))}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;

